/**
 * Response Mapping Utilities
 * 
 * This module provides utilities for mapping TEFAS API responses to typed responses
 * and validating response data.
 */

import { 
  FundHistoryResponse, 
  TefasApiResponse, 
  TefasApiFundData, 
  FundType
} from '../types';
import { ResponseValidationError } from '../errors';

/**
 * Maps a single TEFAS API fund data item to our typed response
 * 
 * @param apiData - Raw fund data from TEFAS API
 * @returns Mapped fund history response
 * @throws ResponseValidationError if data is invalid
 */
export function mapTefasFundData(apiData: TefasApiFundData): FundHistoryResponse {
  try {
    // Validate required fields
    if (!apiData.FONKODU || typeof apiData.FONKODU !== 'string') {
      throw new ResponseValidationError('Missing or invalid fund code');
    }

    if (!apiData.FONUNVAN || typeof apiData.FONUNVAN !== 'string') {
      throw new ResponseValidationError('Missing or invalid fund name');
    }

    if (!apiData.TARIH || typeof apiData.TARIH !== 'string') {
      throw new ResponseValidationError('Missing or invalid date');
    }

    // Validate numeric fields
    if (typeof apiData.FIYAT !== 'number' || apiData.FIYAT < 0) {
      throw new ResponseValidationError('Invalid fund price');
    }

    if (typeof apiData.PORTFOYBUYUKLUK !== 'number' || apiData.PORTFOYBUYUKLUK < 0) {
      throw new ResponseValidationError('Invalid total assets');
    }

    if (typeof apiData.KISISAYISI !== 'number' || apiData.KISISAYISI < 0) {
      throw new ResponseValidationError('Invalid investor count');
    }

    // Convert timestamp to date string (YYYY-MM-DD format)
    const timestamp = parseInt(apiData.TARIH);
    const date = new Date(timestamp);
    const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD format

    const volume = apiData.TEDPAYSAYISI || 0; // Use number of shares as volume
    const investorCount = apiData.KISISAYISI ?? 0;

    // Determine fund type from the API response or use a default
    // This will be overridden by the calling function
    const fundType = FundType.YAT; // Default, will be overridden by caller

    return {
      fundCode: apiData.FONKODU,
      fundName: apiData.FONUNVAN,
      fundType,
      date: dateString,
      price: apiData.FIYAT,
      volume,
      assets: apiData.PORTFOYBUYUKLUK,
      investorCount
    };
  } catch (error) {
    if (error instanceof ResponseValidationError) {
      throw error;
    }
    throw new ResponseValidationError(`Failed to map fund data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Maps TEFAS API response to our typed response array
 * 
 * @param apiResponse - Raw response from TEFAS API
 * @param fundType - Fund type from the request (used to set fund type in response)
 * @returns Array of mapped fund history responses
 * @throws ResponseValidationError if response is invalid
 */
export function mapTefasResponse(apiResponse: TefasApiResponse, fundType: FundType): FundHistoryResponse[] {
  try {
    // Validate response structure
    if (!apiResponse || typeof apiResponse !== 'object') {
      throw new ResponseValidationError('Invalid response structure');
    }

    if (!Array.isArray(apiResponse.data)) {
      throw new ResponseValidationError('Response data is not an array');
    }

    // Map each fund data item
    return apiResponse.data.map((fundData, index) => {
      try {
        const mappedData = mapTefasFundData(fundData);
        // Override the fund type with the one from the request
        mappedData.fundType = fundType;
        return mappedData;
      } catch (error) {
        throw new ResponseValidationError(`Failed to map fund data at index ${index}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    });
  } catch (error) {
    if (error instanceof ResponseValidationError) {
      throw error;
    }
    throw new ResponseValidationError(`Failed to map TEFAS response: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Validates a single fund history response
 * 
 * @param response - Fund history response to validate
 * @returns True if valid, false otherwise
 */
export function validateFundHistoryResponse(response: FundHistoryResponse): boolean {
  try {
    // Check required string fields
    if (!response.fundCode || typeof response.fundCode !== 'string') return false;
    if (!response.fundName || typeof response.fundName !== 'string') return false;
    if (!response.date || typeof response.date !== 'string') return false;

    // Check fund type
    if (!Object.values(FundType).includes(response.fundType)) return false;

    // Check numeric fields
    if (typeof response.price !== 'number' || response.price < 0) return false;
    if (typeof response.volume !== 'number' || response.volume < 0) return false;
    if (typeof response.assets !== 'number' || response.assets < 0) return false;
    if (typeof response.investorCount !== 'number' || response.investorCount < 0) return false;

    // Check date format (basic validation)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(response.date)) return false;

    return true;
  } catch {
    return false;
  }
}

/**
 * Validates an array of fund history responses
 * 
 * @param responses - Array of fund history responses to validate
 * @returns True if all responses are valid, false otherwise
 */
export function validateFundHistoryResponses(responses: FundHistoryResponse[]): boolean {
  if (!Array.isArray(responses)) return false;
  
  return responses.every(validateFundHistoryResponse);
}

/**
 * Filters fund history responses by fund type
 * 
 * @param responses - Array of fund history responses
 * @param fundType - Fund type to filter by
 * @returns Filtered array of responses
 */
export function filterByFundType(responses: FundHistoryResponse[], fundType: FundType): FundHistoryResponse[] {
  return responses.filter(response => response.fundType === fundType);
}

/**
 * Filters fund history responses by fund code
 * 
 * @param responses - Array of fund history responses
 * @param fundCode - Fund code to filter by
 * @returns Filtered array of responses
 */
export function filterByFundCode(responses: FundHistoryResponse[], fundCode: string): FundHistoryResponse[] {
  return responses.filter(response => response.fundCode === fundCode);
}

/**
 * Groups fund history responses by fund code
 * 
 * @param responses - Array of fund history responses
 * @returns Object with fund codes as keys and arrays of responses as values
 */
export function groupByFundCode(responses: FundHistoryResponse[]): Record<string, FundHistoryResponse[]> {
  return responses.reduce((groups, response) => {
    const fundCode = response.fundCode;
    if (!groups[fundCode]) {
      groups[fundCode] = [];
    }
    groups[fundCode].push(response);
    return groups;
  }, {} as Record<string, FundHistoryResponse[]>);
}

/**
 * Sorts fund history responses by date (ascending)
 * 
 * @param responses - Array of fund history responses
 * @returns Sorted array of responses
 */
export function sortByDate(responses: FundHistoryResponse[]): FundHistoryResponse[] {
  return [...responses].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Sorts fund history responses by date (descending)
 * 
 * @param responses - Array of fund history responses
 * @returns Sorted array of responses
 */
export function sortByDateDescending(responses: FundHistoryResponse[]): FundHistoryResponse[] {
  return [...responses].sort((a, b) => b.date.localeCompare(a.date));
}

/**
 * Gets unique fund codes from responses
 * 
 * @param responses - Array of fund history responses
 * @returns Array of unique fund codes
 */
export function getUniqueFundCodes(responses: FundHistoryResponse[]): string[] {
  const fundCodes = new Set(responses.map(response => response.fundCode));
  return Array.from(fundCodes);
}

/**
 * Gets the latest response for each fund
 * 
 * @param responses - Array of fund history responses
 * @returns Object with fund codes as keys and latest responses as values
 */
export function getLatestByFundCode(responses: FundHistoryResponse[]): Record<string, FundHistoryResponse> {
  const sorted = sortByDateDescending(responses);
  const latest: Record<string, FundHistoryResponse> = {};
  
  for (const response of sorted) {
    if (!latest[response.fundCode]) {
      latest[response.fundCode] = response;
    }
  }
  
  return latest;
}

/**
 * Calculates total assets across all funds
 * 
 * @param responses - Array of fund history responses
 * @returns Total assets value
 */
export function calculateTotalAssets(responses: FundHistoryResponse[]): number {
  return responses.reduce((total, response) => total + response.assets, 0);
}

/**
 * Calculates average price across all funds
 * 
 * @param responses - Array of fund history responses
 * @returns Average price
 */
export function calculateAveragePrice(responses: FundHistoryResponse[]): number {
  if (responses.length === 0) return 0;
  const totalPrice = responses.reduce((total, response) => total + response.price, 0);
  return totalPrice / responses.length;
}
